import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "@actinc/dls";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import { Box } from "@mui/material";

import { AlertDialog } from "../AlertDialog";
import { InsertImageDialog } from "../InsertImageDialog";
import { InsertMediaDialog } from "../InsertMediaDialog";
import { patchMathTypePlugin } from "./patchMathTypePlugin";

import { useSessionStore } from "../../services/hooks";

import { UserContext } from "../../userProvider";
import { UsersContext } from "../../usersProvider";

// See patchMathTypePlugin.js for the WIRIS singleton and detached-editor fixes.
patchMathTypePlugin(ClassicEditor.builtinPlugins);

// Smart quote buttons provided by the SymbolShortcuts plugin (CDE-16014). They
// are added to the end of every content toolbar - the item and passage editors
// and the answer choice editors. The names come from the build so a symbol
// added to the plugin's DEFAULT_SYMBOLS shows up here automatically.
if (!ClassicEditor.symbolShortcutItems?.length) {
  console.warn(
    "Editor: ClassicEditor.symbolShortcutItems is missing, so the smart quote " +
      "buttons (CDE-16014) will not be rendered on any toolbar. The CKEditor " +
      "build artifact is stale - rebuild client/ckeditor5-build-cde-classic.",
  );
}

const SMART_QUOTE_ITEMS = ClassicEditor.symbolShortcutItems ?? [];

// Options that build their own toolbar from scratch rather than adding to the
// content one. Smart quotes do not belong on those toolbars, so their presence
// suppresses the append below.
const TOOLBAR_REPLACING_OPTIONS = [
  "blockMatch",
  "blockOrder",
  "embeddedInlineContent",
  "inlineChoice",
  "taskComments",
];

// The editors that get the full content toolbar - and with it the content
// specific plugins, events and smart quotes.
const isContentEditor = (options) =>
  options.includes("content") || options.includes("referenceContent");

const shouldShowSmartQuotes = (options) =>
  isContentEditor(options) &&
  !options.some((option) => TOOLBAR_REPLACING_OPTIONS.includes(option));

const closeDropdown = (panel) =>
  panel.closest(".ck-dropdown")?.querySelector(".ck-dropdown__button")?.click();

const manageDropdownOverlay = ({ overlayClassName, matchPanel, wireItems }) => {
  let overlay = null;
  let activePanel = null;
  let originalOpacity = "";

  const clearActivePanel = () => {
    if (activePanel) {
      activePanel.style.opacity = originalOpacity;
    }
    activePanel = null;
    originalOpacity = "";
  };

  const handleMutation = (panel) => {
    const isVisible = panel.classList.contains("ck-dropdown__panel-visible");
    if (isVisible && matchPanel(panel)) {
      requestAnimationFrame(() => {
        if (!panel.classList.contains("ck-dropdown__panel-visible")) return;
        if (overlay) overlay.remove();
        clearActivePanel();

        const rect = panel.getBoundingClientRect();
        overlay = document.createElement("div");
        overlay.className = overlayClassName;
        Object.assign(overlay.style, {
          top: `${rect.top}px`,
          left: `${rect.left}px`,
        });
        overlay.addEventListener("mousedown", (e) => {
          e.stopPropagation();
          e.preventDefault();
        });

        const clone = panel.cloneNode(true);
        Object.assign(clone.style, {
          position: "static",
          display: "block",
          border: "none",
          boxShadow: "none",
        });
        overlay.appendChild(clone);
        wireItems(clone, panel);

        document.body.appendChild(overlay);
        originalOpacity = panel.style.opacity;
        panel.style.opacity = "0";
        activePanel = panel;
      });
    } else if (panel === activePanel && !isVisible) {
      if (overlay) {
        overlay.remove();
        overlay = null;
      }
      clearActivePanel();
    }
  };

  const destroy = () => {
    if (overlay) overlay.remove();
    clearActivePanel();
  };

  return { handleMutation, destroy };
};

const APPLY_TEMPLATE_BUTTON_LABEL = "Apply Template";

const Editor = ({
  _actions = [],
  assets,
  commentsPath,
  contentArea,
  data,
  deleteInteraction,
  functions,
  id,
  interactions = [],
  minHeight,
  onBlur,
  onChange,
  onReady,
  options = [],
  readonly,
  suggestionsPath,
  templates = [],
  type,
}) => {
  const user = useContext(UserContext);
  const users = useContext(UsersContext);

  const [insertApplyTemplateDialogOpen, setInsertApplyTemplateDialogOpen] =
    useState(false);
  const [insertImageDialogOpen, setInsertImageDialogOpen] = useState(false);
  const [insertMediaDialogOpen, setInsertMediaDialogOpen] = useState(false);
  const [editor, setEditor] = useState(null);

  const templatesRef = useRef(templates);
  useEffect(() => {
    templatesRef.current = templates;
  }, [templates]);

  const [workspace, workspaceStore] = useSessionStore("workspace");

  useEffect(() => {
    if (!editor || !isContentEditor(options)) return;
    editor.on("cde:openApplyTemplate", (_eventInfo, template) => {
      openApplyTemplateDialog(template);
    });
    editor.on("cde:openInsertImageDialog", (_eventInfo) => {
      openInsertImageDialog(editor);
    });
    editor.on("cde:openInsertMediaDialog", (_eventInfo) => {
      openInsertMediaDialog(editor);
    });
    editor.on("cde:setShowBlocks", (_eventInfo, ...args) => {
      setContentEditorsState(_eventInfo, "showBlocks", args[0]);
    });
    editor.on("cde:setShowCharacters", (_eventInfo, ...args) => {
      setContentEditorsState(_eventInfo, "showCharacters", args[0]);
    });
    return () => {
      editor.off("cde:openApplyTemplate");
      editor.off("cde:openInsertImageDialog");
      editor.off("cde:openInsertMediaDialog");
      editor.off("cde:setShowBlocks");
      editor.off("cde:setShowCharacters");
    };
  }, [editor, options, workspace]);

  useEffect(() => {
    if (!editor || !isContentEditor(options)) return;
    Object.keys(workspace.contentEditorsState).forEach((property) => {
      editor.fire(`cde:${property}`, workspace.contentEditorsState[property]);
    });
  }, [editor, options, workspace]);

  useEffect(() => {
    if (!editor) return;
    if (readonly) editor.enableReadOnlyMode(`${id}-readonly`);
    if (!readonly) editor.disableReadOnlyMode(`${id}-readonly`);
  }, [editor, readonly, id]);

  useEffect(() => {
    if (!editor || !options.includes("applyTemplate")) return;
    editor.fire("cde:setApplyTemplates", templates);
  }, [editor, options, templates]);

  const closeApplyTemplateDialog = (bool) => {
    if (bool === true) {
      if (interactions.length) {
        interactions.forEach((interaction, index) => {
          deleteInteraction(interaction)(index)();
        });
      }
      editor.data.set(insertApplyTemplateDialogOpen);
    }
    setInsertApplyTemplateDialogOpen(false);
  };
  const closeInsertImageDialog = () => {
    setInsertImageDialogOpen(false);
  };
  const closeInsertMediaDialog = () => {
    setInsertMediaDialogOpen(false);
  };
  const handleReady = (ed) => {
    if (!ed) return;

    setEditor(ed);

    if (minHeight) {
      ed.editing.view.change((writer) =>
        writer.setStyle(
          "height",
          Number.isInteger(minHeight) ? `${minHeight}px` : minHeight,
          ed.editing.view.document.getRoot(),
        ),
      );
    }

    const editorElement = ed.ui.view.element;

    const isApplyTemplatePanel = (panel) => {
      const button = panel
        .closest(".ck-dropdown")
        ?.querySelector(".ck-dropdown__button");
      const label =
        button?.getAttribute("aria-label") ??
        button?.getAttribute("data-cke-tooltip-text");
      return label === APPLY_TEMPLATE_BUTTON_LABEL;
    };

    const insertTableManager = manageDropdownOverlay({
      overlayClassName: "ck-insert-table-overlay",
      matchPanel: (panel) =>
        !!panel.querySelector(".ck-insert-table-dropdown__grid"),
      wireItems: (clone, panel) => {
        const boxes = clone.querySelectorAll(
          ".ck-insert-table-dropdown-grid-box",
        );
        boxes.forEach((box, i) => {
          const rows = Math.floor(i / 10) + 1;
          const columns = (i % 10) + 1;
          box.addEventListener("mouseover", () => {
            boxes.forEach((b, j) => {
              b.classList.toggle(
                "ck-on",
                Math.floor(j / 10) + 1 <= rows && (j % 10) + 1 <= columns,
              );
            });
            const label = clone.querySelector(
              ".ck-insert-table-dropdown__label",
            );
            if (label) label.textContent = `${rows} × ${columns}`;
          });
          box.addEventListener("click", (e) => {
            e.preventDefault();
            ed.execute("insertTable", { rows, columns });
            ed.editing.view.focus();
            closeDropdown(panel);
          });
        });
      },
    });

    const applyTemplateManager = manageDropdownOverlay({
      overlayClassName: "ck-apply-template-overlay",
      matchPanel: isApplyTemplatePanel,
      wireItems: (clone, panel) => {
        const buttons = clone.querySelectorAll(".ck-list__item .ck-button");
        buttons.forEach((button, i) => {
          button.addEventListener("click", (e) => {
            e.preventDefault();
            const template = templatesRef.current?.[i];
            if (template) ed.execute("applyTemplate", template.html);
            ed.editing.view.focus();
            closeDropdown(panel);
          });
        });
      },
    });

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== "class") continue;
        const panel = m.target;
        if (!panel.classList.contains("ck-dropdown__panel")) continue;
        insertTableManager.handleMutation(panel);
        applyTemplateManager.handleMutation(panel);
      }
    });

    observer.observe(editorElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    ed.on("destroy", () => {
      insertTableManager.destroy();
      applyTemplateManager.destroy();
      observer.disconnect();
    });

    onReady && onReady(ed);
  };

  useEffect(() => {
    if (!editor) return;
    if (options.includes("acceptChanges")) {
      ["trackChanges"].forEach((cmd) => {
        const command = editor.commands.get(cmd);
        if (command) command.forceDisabled("suggestionsMode");
      });
    }
    if (options.includes("suggestChanges")) {
      // Turn on Track Changes
      editor.execute("trackChanges");
      // Turn off commands the user should not be able to select/toggle
      ["acceptSuggestion", "acceptAllSuggestions", "trackChanges"].forEach(
        (cmd) => {
          const command = editor.commands.get(cmd);
          if (command) command.forceDisabled("suggestionsMode");
        },
      );
    }
    if (
      !options.includes("acceptChanges") &&
      !options.includes("suggestChanges")
    ) {
      [
        "acceptSuggestion",
        "acceptAllSuggestions",
        "discardSuggestion",
        "discardAllSuggestions",
        "trackChanges",
      ].forEach((cmd) => {
        const command = editor.commands.get(cmd);
        if (command) command.forceDisabled("suggestionsMode");
      });
    }
  }, [editor, options]);

  useEffect(() => {
    if (!editor) return;
    if (
      !options.includes("suggestChanges") &&
      !options.includes("acceptChanges")
    )
      return;
    if (!editor.plugins.has("Annotations")) return;

    const annotations = editor.plugins.get("Annotations");
    const deactivate = () => annotations.deactivateAll();

    const acceptCmd = editor.commands.get("acceptSuggestion");
    const onAccept = () => setTimeout(deactivate, 0);
    if (acceptCmd) acceptCmd.on("execute", onAccept, { priority: "lowest" });

    const findTrackChangesDropdown = () => {
      const items = editor.ui?.view?.toolbar?.items;
      if (!items) return null;
      for (const item of items) {
        const bv = item?.buttonView;
        if (bv?.commandName === "trackChanges") return item;
        const cls = item?.class;
        if (typeof cls === "string" && cls.includes("ck-track-changes"))
          return item;
        if (bv?.label === "Track changes" || bv?.tooltip === "Track changes")
          return item;
      }
      return null;
    };

    const onDropdownOpen = (_evt, _name, isOpen) => {
      if (isOpen) deactivate();
    };

    const dropdown = findTrackChangesDropdown();
    if (dropdown?.on) dropdown.on("change:isOpen", onDropdownOpen);

    return () => {
      if (acceptCmd) acceptCmd.off("execute", onAccept);
      if (dropdown?.off) dropdown.off("change:isOpen", onDropdownOpen);
    };
  }, [editor, options]);

  const openApplyTemplateDialog = (template) => {
    if (editor.data.get()) {
      setInsertApplyTemplateDialogOpen(template);
    } else {
      editor.data.set(template);
      setInsertApplyTemplateDialogOpen(false);
    }
  };
  const alertDialogProps = {
    confirmButtonText: "Overwrite",
    dialogTitle: "Overwrite Content",
    draggable: false,
    fullWidth: true,
    maxWidth: "xs",
    message: (
      <Alert severity="warning">
        Applying a template will overwrite all existing content
      </Alert>
    ),
    onClose: closeApplyTemplateDialog,
    open: !!insertApplyTemplateDialogOpen,
    scroll: "paper",
    title: "Apply Template",
  };
  const openInsertImageDialog = (ed) => {
    setInsertImageDialogOpen(ed);
  };
  const openInsertMediaDialog = (ed) => {
    setInsertMediaDialogOpen(ed);
  };
  const setContentEditorsState = (
    _eventInfo,
    property,
    newValue,
    _oldValue,
  ) => {
    const current = workspaceStore.get();
    workspaceStore.set({
      ...current,
      contentEditorsState: {
        ...current.contentEditorsState,
        [property]: newValue,
      },
    });
  };

  // Memoized so the CKEditor component keeps receiving the same config object
  // (and the same toolbar item array) across re-renders of the parent card.
  const config = useMemo(() => {
    const baseConfig = [
      ...options,
      typeof jest !== "undefined" ? "removeMathType" : "",
    ]
      .filter(Boolean)
      .reduce(
        (acc, option) => {
          const {
            toolbar: { items, ...toolbar },
            ...rest
          } = acc;
          switch (option) {
            case "acceptChanges":
            case "suggestChanges":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: [...items, "|", "trackChanges"],
                },
              };
            case "applyTemplate":
              return {
                ...rest,
                // Custom config key consumed by the ApplyTemplate plugin via
                // editor.config.get("applyTemplate"). Kept in sync after init by
                // the cde:setApplyTemplates event below.
                applyTemplate: templates,
                toolbar: {
                  ...toolbar,
                  items: [...items, "|", "applyTemplate"],
                },
              };
            case "blockMatch":
            case "blockOrder": {
              let blockDefaults = [
                ...items,
                "|",
                "insertImage",
                "|",
                "applyNoBreak",
                "|",
                "showBlocks",
                "showCharacters",
              ];
              blockDefaults = [...blockDefaults]
                .toString()
                .replace("fontSize", "blockStyles,|,fontSize")
                .split(",");

              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: blockDefaults,
                },
              };
            }
            case "content": {
              let contentDefaults = [
                ...items,
                "|",
                "insertImage",
                "|",
                "applyCBT",
                "applyPBT",
                "applyNoBreak",
                "|",
                "showBlocks",
                "showCharacters",
              ];
              contentDefaults = [...contentDefaults]
                .toString()
                .replace("fontSize", "blockStyles,|,fontSize")
                .split(",");

              if (type === "passage")
                contentDefaults = [...contentDefaults]
                  .toString()
                  .replace(
                    "subscript",
                    "subscript,passageUnderline,horizontalLine",
                  )
                  .split(",");
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: _actions.includes("viewSource")
                    ? [...contentDefaults, "|", "sourceEditing"]
                    : contentDefaults,
                },
              };
            }
            case "createHighlight":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: [...items, "|", "createHighlight"],
                },
              };
            case "createInteraction":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: [...items, "|", "createInteraction"],
                },
              };
            case "createLineRef":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: [...items, "|", "createLineRef"],
                },
              };
            case "embeddedInlineContent":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: [
                    "bold",
                    "italic",
                    "underline",
                    "|",
                    "specialCharacters",
                    "|",
                    "applyNoBreak",
                    "|",
                    "showCharacters",
                  ],
                },
              };
            case "inlineChoice":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: ["specialCharacters"],
                },
              };
            case "insertMedia":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: [
                    ...items
                      .toString()
                      .replace("insertImage", "insertImage,insertMedia")
                      .split(","),
                  ],
                },
              };
            case "referenceContent": {
              const isReview = options?.includes("suggestChanges");
              let refDefaults = [...items, "|", "insertImage", "|"];

              if (isReview) {
                refDefaults.push(
                  "applyCBT",
                  "applyPBT",
                  "applyNoBreak",
                  "|",
                  "showBlocks",
                  "showCharacters",
                );
              } else {
                refDefaults.push(
                  "showBlocks",
                  "showCharacters",
                  "|",
                  "deleteReferenceContent",
                );
              }
              refDefaults = [...refDefaults]
                .toString()
                .replace("fontSize", "blockStyles,|,fontSize")
                .split(",");
              if (type === "passage" && isReview)
                refDefaults = [...refDefaults]
                  .toString()
                  .replace(
                    "subscript",
                    "subscript,passageUnderline,horizontalLine",
                  )
                  .split(",");
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: !isReview
                    ? refDefaults
                    : [...refDefaults, "|", "sourceEditing"],
                },
              };
            }
            case "removeMathType":
              return {
                ...rest,
                removePlugins: [...rest.removePlugins, "MathType"],
                toolbar: {
                  ...toolbar,
                  items: [
                    ...items
                      .toString()
                      .replace(",|,MathType,ChemType", "")
                      .split(","),
                  ],
                },
              };
            case "sourceEditing":
              return {
                ...rest,
                toolbar: {
                  ...toolbar,
                  items: _actions.includes("viewSource")
                    ? [...items, "|", "sourceEditing"]
                    : items,
                },
              };
            case "taskComments":
              return {
                ...rest,
                toolbar: {
                  items: [
                    "undo",
                    "redo",
                    "|",
                    "bulletedList",
                    "numberedList",
                    "|",
                    "MathType",
                    "ChemType",
                  ],
                },
              };
            default:
              return { ...acc };
          }
        },
        // Initial config (accumulator)
        {
          collaboration: {
            comments: commentsPath ? functions.getValues(commentsPath) : [],
            suggestions: suggestionsPath
              ? functions.getValues(suggestionsPath)
              : [],
            userId: user?.id || null,
            users: users?.map(({ id, name }) => ({ id, name })) || [],
          },
          comments: {
            editorConfig: {},
          },
          fontSize: {
            options: [
              "default",
              {
                title: "18 Pt Font",
                model: "18pt",
              },
            ],
          },
          heading: {
            options: [
              {
                model: "paragraph",
                title: "Paragraph",
                class: "ck-heading_paragraph",
              },
              {
                model: "chapterTitle",
                view: "ct",
                title: "Chapter Title",
              },
              {
                model: "heading1",
                view: "h3",
                title: "Heading 3",
                class: "ck-heading_heading3",
              },
              {
                model: "heading2",
                view: "h4",
                title: "Heading 4",
                class: "ck-heading_heading4",
              },
              {
                model: "heading3",
                view: "h5",
                title: "Heading 5",
                class: "ck-heading_heading5",
              },
            ],
          },
          htmlSupport: {
            allow: [
              {
                name: /.*/,
                attributes: true,
                classes: true,
                styles: true,
              },
            ],
          },
          image: {
            insert: {
              type: "inline",
            },
          },
          language: "en",
          // CKEditor 5 production license key, v44.0.0
          licenseKey:
            "eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MTcyNTExOTksImp0aSI6IjBkODQ2Yjc1LTRmMzctNGZmMi04MjM0LTM5Y2EzMzRjNDNmMSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIkRPIiwiRlAiLCJTQyIsIlRPQyIsIlRQTCIsIlBPRSIsIkNDIiwiTUYiLCJTRUUiLCJFQ0giLCJFSVMiLCJMSCIsIkZPTyIsIkNNVCIsIlRDIiwiTFRTIl0sInZjIjoiMzhjNjUxNTcifQ.nu7z6cOqp_SEsgk-xr5mFPvBBOdmCviBFXouyxaZOm5qA1tr1qGSsthHh6Uf_JHnkrOT2qpfzCf_vhBYtitweA",
          //"eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3ODU2Mjg3OTksImp0aSI6IjhkZWM4MDRlLWFmMmItNGE3OS04YzFjLTQ1NTI4NWQ0NTc3ZiIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIkRPIiwiRlAiLCJTQyIsIlRPQyIsIlRQTCIsIlBPRSIsIkNDIiwiTUYiLCJTRUUiLCJFQ0giLCJFSVMiLCJMSCIsIlRDIiwiQ01UIiwiQUkiXSwidmMiOiIxYWU0NmNkYiJ9.58bOJel0K5f8CsDn8sWvZR1ObJlIlSdGztyo5hcg51twc_2Vox0OF_sUKW9rQHMwHE6k7sQIjaEWllL-_yfG5g",
          list: {
            properties: {
              styles: true,
            },
          },
          mathTypeParameters: {
            serviceProviderProperties: {
              URI: "https://www.wiris.net/demo/plugins/app/configurationjs",
              server: "java",
            },
          },
          media: {
            insert: {
              type: "inline",
            },
          },
          object: {
            assets,
            type,
          },
          removePlugins: [],
          style: {
            definitions: [
              {
                classes: ["passage-underline"],
                element: "span",
                name: "Passage Underline",
              },
            ],
          },
          toolbar: {
            items: [
              "undo",
              "redo",
              "|",
              "fontSize",
              "|",
              "bold",
              "italic",
              "underline",
              "superscript",
              "subscript",
              "|",
              "alignment:left",
              "alignment:center",
              "alignment:right",
              "alignment:justify",
              "|",
              "bulletedList",
              "numberedList",
              "|",
              "insertTable",
              "|",
              "specialCharacters",
              "|",
              "MathType",
              "ChemType",
              //"wproofreader",
            ],
            shouldNotGroupWhenFull: true,
          },
          wproofreader: {
            serviceId: "8Ub89AMI3UY3iDC",
            srcUrl:
              "https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js",
          },
        },
      );

    // The smart quotes are appended after the reduce, not inside the
    // `content`/`referenceContent` cases, because the acceptance criteria put
    // them last on the toolbar: options listed after the content one (for
    // example `createLineRef` or `suggestChanges`) still add their own buttons
    // and those have to come first.
    return shouldShowSmartQuotes(options) && SMART_QUOTE_ITEMS.length > 0
      ? {
          ...baseConfig,
          toolbar: {
            ...baseConfig.toolbar,
            items: [...baseConfig.toolbar.items, "|", ...SMART_QUOTE_ITEMS],
          },
        }
      : baseConfig;
  }, [
    _actions,
    assets,
    commentsPath,
    functions,
    options,
    suggestionsPath,
    templates,
    type,
    user,
    users,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        "& img.Wirisformula": {
          pointerEvents: "all",
        },
      }}
    >
      <CKEditor
        className="ck-content"
        config={config}
        data={data}
        editor={ClassicEditor}
        id={id}
        onBlur={onBlur}
        onChange={onChange}
        onReady={handleReady}
      />
      {!!insertApplyTemplateDialogOpen && (
        <AlertDialog {...alertDialogProps}></AlertDialog>
      )}
      {options.some((option) =>
        ["blockMatch", "blockOrder", "content", "referenceContent"].includes(
          option,
        ),
      ) &&
        !!insertImageDialogOpen && (
          <InsertImageDialog
            editor={insertImageDialogOpen}
            images={[...(assets?.images ?? [])]}
            open={!!insertImageDialogOpen}
            onClose={closeInsertImageDialog}
          />
        )}
      {options.includes("content") && !!insertMediaDialogOpen && (
        <InsertMediaDialog
          editor={insertMediaDialogOpen}
          mediaAssets={[...(assets?.medias ?? [])]}
          open={!!insertMediaDialogOpen}
          onClose={closeInsertMediaDialog}
        />
      )}
    </Box>
  );
};

export { Editor };
